import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { tap, map, catchError } from 'rxjs/operators';
import { TokenEncryptionService } from 'src/app/Services/token-encryption.service';
import { ProjectService } from 'src/app/Services/project.service';
import { config } from '../config';
import { hasCompanyRole } from '../utils/role.utils';

export interface OnboardingStep {
  id: string;
  label: string;
  description: string;
  completed: boolean;
  route?: string;
}

export interface OnboardingState {
  isOnboarding: boolean;
  currentStepIndex: number;
  steps: OnboardingStep[];
  skipped: boolean;
  minimized: boolean;
}

export interface TrackingModeResponse {
  success: boolean;
  message: string;
  selected_mode: string;
  next_step: string;
}

export type CleanupDecision = 'keep' | 'remove';

export interface CleanupDecisionRequest {
  decision: CleanupDecision;
  private_test_employee_id: number;
  first_employee_id: number;
}

export interface CleanupDecisionResponse {
  success: boolean;
  decision: CleanupDecision;
  result: 'kept' | 'removed' | 'already_removed';
  private_test_employee_id: number | null;
  first_employee_id: number;
  state: Record<string, unknown>;
  v2?: Record<string, unknown>;
}

const STORAGE_KEY = 'supersee_onboarding_checklist';
const TRACKING_MODE_KEY = 'supersee_tracking_mode';
const CREATED_EMPLOYEE_KEY = 'supersee_onboarding_employee';
const PERMANENT_COMPLETION_KEY = 'supersee_onboarding_completed';

const DEFAULT_STEPS: OnboardingStep[] = [
  {
    id: 'mode_selection',
    label: 'Choose Tracking Mode',
    description: 'Select Hidden, Manual, or Automatic mode',
    completed: false,
    route: '/setup'
  },
  {
    id: 'create_employee',
    label: 'Add Your First Employee',
    description: 'Add employee details and select their operating system',
    completed: false,
    route: '/setup'
  },
  {
    id: 'install_agent',
    label: 'Install SuperSee on a Device',
    description: 'Download and install the SuperSee agent',
    completed: false,
    route: '/setup'
  },
  {
    id: 'request_screenshot',
    label: 'Capture First Activity',
    description: 'Test the screenshot capture feature',
    completed: false,
    route: '/screenshots'
  }
];

@Injectable({
  providedIn: 'root'
})
export class OnboardingChecklistService {
  private apiUrl = config.baseUrl + "supersee/v1/onboarding";
  private apiUrlV2 = config.baseUrl + "supersee/v1/onboarding/v2";
  private stateSubject = new BehaviorSubject<OnboardingState>(this.loadState());
  state$ = this.stateSubject.asObservable();
  private hideForSessionSubject = new BehaviorSubject<boolean>(false);
  hideForSession$ = this.hideForSessionSubject.asObservable();
  private isPanelExpandedSubject = new BehaviorSubject<boolean>(false);
  isPanelExpanded$ = this.isPanelExpandedSubject.asObservable();
  private trackingModeSubject = new BehaviorSubject<string | null>(null);
  trackingMode$ = this.trackingModeSubject.asObservable();
  private eligibleSubject = new BehaviorSubject<boolean>(true);
  eligible$ = this.eligibleSubject.asObservable();

  constructor(
    private http: HttpClient,
    private tokenEncryptionService: TokenEncryptionService,
    private projectService: ProjectService
  ) {
    this.loadTrackingMode();
  }

  private loadTrackingMode(): void {
    // Just load from localStorage - no need for separate API call
    const localMode = this.getTrackingMode();
    this.trackingModeSubject.next(localMode);
  }

  private loadState(): OnboardingState {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Migrate: if stored state has more steps than current DEFAULT_STEPS, trim to match
        const validStepIds = DEFAULT_STEPS.map(s => s.id);
        if (parsed.steps && parsed.steps.length > DEFAULT_STEPS.length) {
          parsed.steps = parsed.steps.filter(s => validStepIds.includes(s.id));
          if (parsed.currentStepIndex >= parsed.steps.length) {
            parsed.currentStepIndex = parsed.steps.length - 1;
          }
          localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
        }
        return parsed;
      }
    } catch (error) {
      console.error('[OnboardingChecklistService] Error loading state:', error);
    }
    return this.getDefaultState();
  }

  private getDefaultState(): OnboardingState {
    return {
      isOnboarding: true,
      currentStepIndex: 0,
      steps: JSON.parse(JSON.stringify(DEFAULT_STEPS)),
      skipped: false,
      minimized: false
    };
  }

  private saveState(state: OnboardingState): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      this.stateSubject.next(state);

      // If all steps are completed, persist a permanent completion flag.
      // This ensures the floating checklist stays hidden across logout/login sessions
      // (even when other localStorage keys are cleared and rebuilt).
      if (state.steps && state.steps.length > 0 && state.steps.every(s => s.completed)) {
        localStorage.setItem(PERMANENT_COMPLETION_KEY, 'true');
      }
    } catch (error) {
      console.error('[OnboardingChecklistService] Error saving state:', error);
    }
  }

  /** Mark classic + journey onboarding finished for guards and floating checklist. */
  setPermanentlyCompleted(): void {
    try {
      localStorage.setItem(PERMANENT_COMPLETION_KEY, 'true');
    } catch (error) {
      console.error('[OnboardingChecklistService] Error saving permanent completion:', error);
    }
  }

  /** True after setup wizard (incl. successful screenshot) marked complete — used by route guards. */
  isPermanentlyCompleted(): boolean {
    try {
      if (localStorage.getItem(PERMANENT_COMPLETION_KEY) === 'true') {
        return true;
      }
    } catch {
      /* ignore */
    }
    return this.isAllCompleted();
  }

  saveTrackingMode(mode: string): Observable<any> {
    return this.completeStep('mode_selection', { mode: mode });
  }

  completeStep(stepId: string, data?: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/step`, {
      step_id: stepId,
      action: 'complete',
      data: data
    }).pipe(
      tap(response => {
        if (response.success) {
          // Update local state
          this.completeStepLocal(stepId);

          // For mode selection, also update tracking mode
          if (stepId === 'mode_selection' && data?.mode) {
            this.trackingModeSubject.next(data.mode);
            localStorage.setItem(TRACKING_MODE_KEY, data.mode);
          }
        }
      })
    );
  }

  getNextStep(): Observable<any> {
    // Debug: Check token and URL
    const token = this.tokenEncryptionService.getToken();

    return this.http.get(`${this.apiUrl}/next-step`);
  }

  /** Activation Journey — create or reuse admin private-test employee. */
  createOrReuseTestEmployee(payload?: {
    email?: string;
    password?: string;
    reset_password?: boolean;
  }): Observable<any> {
    return this.http.post(`${this.apiUrlV2}/test-employee`, payload || {});
  }

  getV2State(): Observable<any> {
    return this.http.get(`${this.apiUrlV2}/state`);
  }

  updateV2State(body: Record<string, unknown>): Observable<any> {
    return this.http.post(`${this.apiUrlV2}/state`, body || {});
  }

  completeV2Onboarding(source: string = 'screenshot_confirmed'): Observable<any> {
    return this.http.post(`${this.apiUrlV2}/complete`, { source });
  }

  /** Persist keep / remove for private-test cleanup after a real employee exists. */
  saveCleanupDecision(
    decision: CleanupDecision,
    privateTestEmployeeId: number,
    firstEmployeeId: number
  ): Observable<CleanupDecisionResponse> {
    const body: CleanupDecisionRequest = {
      decision,
      private_test_employee_id: privateTestEmployeeId,
      first_employee_id: firstEmployeeId,
    };
    return this.http.post<CleanupDecisionResponse>(`${this.apiUrlV2}/cleanup`, body);
  }

  // Sync local state with backend so the floating checklist shows the correct step
  syncStateFromBackend(): Observable<any> {
    return this.getNextStep().pipe(
      tap({
        next: (response: any) => {

          if (response && response.next_step) {
            const state = this.getState();
            const stepId = response.next_step.step_id;

            // Mark all steps before the current one as completed
            // Backend auto-detection has already marked completed steps
            const stepIds = state.steps.map(s => s.id);
            const currentIndex = stepIds.indexOf(stepId);


            if (currentIndex !== -1) {
              // Mark all previous steps as completed
              state.steps.forEach((s, i) => {
                if (i < currentIndex) {
                  s.completed = true;
                }
              });
              state.currentStepIndex = currentIndex;
              this.saveState(state);
            }
          } else if (response && response.completed) {
            // All steps completed - backend detected everything is done
            const state = this.getState();
            state.steps.forEach(s => s.completed = true);
            this.saveState(state);
            this.setPermanentlyCompleted();
          }
        },
        error: (err: any) => {
          console.error('[OnboardingSync] Error from backend:', err);
          if (err?.status === 404) {
            this.finalizeWhenNextStepUnavailable('404 from next-step sync');
          }
        }
      })
    );
  }

  /**
   * When GET next-step returns 404 (orphaned DB state), treat onboarding as finished for UX
   * so users are not blocked — aligns floating checklist + full-page setup recovery.
   */
  finalizeWhenNextStepUnavailable(reason?: string): void {
    if (reason) {
      console.warn('[Onboarding] finalizeWhenNextStepUnavailable:', reason);
    }
    const state = this.getState();
    state.steps.forEach(s => s.completed = true);
    this.saveState(state);
    this.setPermanentlyCompleted();
  }

  setTrackingMode(mode: 'hidden' | 'employee_controlled' | 'admin_controlled'): void {
    try {
      localStorage.setItem(TRACKING_MODE_KEY, mode);
      this.trackingModeSubject.next(mode);
    } catch (error) {
      console.error('[OnboardingChecklistService] Error saving tracking mode:', error);
    }
  }

  getTrackingMode(): string | null {
    return this.trackingModeSubject.value || localStorage.getItem(TRACKING_MODE_KEY);
  }

  getCurrentUser(): any {
    return this.tokenEncryptionService.getEncryptedItem<any>('user');
  }

  getState(): OnboardingState {
    return this.stateSubject.getValue();
  }

  completeStepLocal(stepId: string): void {
    const state = this.getState();
    const stepIndex = state.steps.findIndex(s => s.id === stepId);
    if (stepIndex !== -1 && !state.steps[stepIndex].completed) {
      state.steps[stepIndex].completed = true;
      // Move to next incomplete step
      const nextIncomplete = state.steps.findIndex((s, i) => i > stepIndex && !s.completed);
      if (nextIncomplete !== -1) {
        state.currentStepIndex = nextIncomplete;
      }
      this.saveState(state);
    }
  }

  completeStepLocalOnly(stepId: string): void {
    this.completeStepLocal(stepId);
  }

  goToStep(stepIndex: number): void {
    const state = this.getState();
    if (stepIndex >= 0 && stepIndex < state.steps.length) {
      state.currentStepIndex = stepIndex;
      this.saveState(state);
    }
  }

  skipOnboarding(): void {
    const state = this.getState();
    state.skipped = true;
    this.saveState(state);
  }

  resumeOnboarding(): void {
    const state = this.getState();
    state.skipped = false;
    this.saveState(state);
  }

  toggleMinimized(): void {
    const state = this.getState();
    state.minimized = !state.minimized;
    this.saveState(state);
  }

  setMinimized(minimized: boolean): void {
    const state = this.getState();
    state.minimized = minimized;
    this.saveState(state);
  }

  resetOnboarding(): void {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(CREATED_EMPLOYEE_KEY);
    localStorage.removeItem(PERMANENT_COMPLETION_KEY);
    this.stateSubject.next(this.getDefaultState());
  }

  /** Restart Journey: classic completion must not keep blocking /setup/ready. */
  clearPermanentlyCompleted(): void {
    try {
      localStorage.removeItem(PERMANENT_COMPLETION_KEY);
    } catch {
      /* ignore */
    }
  }

  // Created employee persistence
  saveCreatedEmployee(employee: any, password: string): void {
    try {
      localStorage.setItem(CREATED_EMPLOYEE_KEY, JSON.stringify({ employee, password }));
    } catch (error) {
      console.error('[OnboardingChecklistService] Error saving created employee:', error);
    }
  }

  getCreatedEmployee(): { employee: any; password: string } | null {
    try {
      const stored = localStorage.getItem(CREATED_EMPLOYEE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('[OnboardingChecklistService] Error loading created employee:', error);
    }
    return null;
  }

  clearCreatedEmployee(): void {
    localStorage.removeItem(CREATED_EMPLOYEE_KEY);
  }

  // Reset back to create_employee step (e.g. when employee was deleted)
  resetToCreateEmployee(): void {
    const state = this.getState();
    // Un-complete create_employee, install_agent, request_screenshot
    state.steps.forEach(s => {
      if (s.id !== 'mode_selection') {
        s.completed = false;
      }
    });
    const createIndex = state.steps.findIndex(s => s.id === 'create_employee');
    state.currentStepIndex = createIndex !== -1 ? createIndex : 1;
    this.saveState(state);
    this.clearCreatedEmployee();
  }

  getCompletedCount(): number {
    return this.getState().steps.filter(s => s.completed).length;
  }

  getTotalSteps(): number {
    return this.getState().steps.length;
  }

  isAllCompleted(): boolean {
    return this.getCompletedCount() === this.getTotalSteps();
  }

  /**
   * Used by the floating checklist CTA to decide between:
   * - "Start setup" (fresh state, no meaningful progress)
   * - "Resume setup" (user has started / made progress / skipped)
   */
  hasSetupStarted(state: OnboardingState = this.getState()): boolean {
    if (!state) return false;

    const completedCount = (state.steps || []).filter(s => s.completed).length;
    if (completedCount > 0) return true;

    // If the backend/front-end advanced the step index, treat it as "started".
    if (typeof state.currentStepIndex === 'number' && state.currentStepIndex > 0) return true;

    // If user explicitly skipped, there's still meaningful progress in the flow.
    if (state.skipped) return true;

    return false;
  }

  getCurrentStep(): OnboardingStep | null {
    const state = this.getState();
    return state.steps[state.currentStepIndex] || null;
  }

  // Session-based hide methods
  hideForSession(): void {
    this.hideForSessionSubject.next(true);
  }

  showForSession(): void {
    this.hideForSessionSubject.next(false);
  }

  isHiddenForSession(): Observable<boolean> {
    return this.hideForSessionSubject.asObservable();
  }

  // Panel expansion methods
  setPanelExpanded(expanded: boolean): void {
    this.isPanelExpandedSubject.next(expanded);
  }

  isPanelExpanded(): Observable<boolean> {
    return this.isPanelExpandedSubject.asObservable();
  }

  // Eligibility check: trial user, <5 employees, no users in invited state
  checkEligibility(): Observable<boolean> {
    const user = this.getCurrentUser();
    if (!hasCompanyRole(user)) {
      this.eligibleSubject.next(false);
      return of(false);
    }
    if (!user || !user.meta || user.meta.quota_status !== 'trial') {
      this.eligibleSubject.next(false);
      return this.eligibleSubject.asObservable();
    }

    return this.projectService.getUserList(0, 0, true).pipe(
      map((users: any[]) => {
        const employees = users || [];
        const totalEmployees = employees.length;

        // Skip invited-user check if onboarding is still in progress
        const onboardingInProgress = !this.isAllCompleted();
        const hasInvitedUser = onboardingInProgress ? false : employees.some(u =>
          !u.status || u.status === 'not-using'
        );

        const eligible = totalEmployees < 5 && !hasInvitedUser;
        this.eligibleSubject.next(eligible);
        return eligible;
      })
    );
  }

  isEligible(): boolean {
    return this.eligibleSubject.getValue();
  }

  /**
   * True when the company already has at least one employee (invited or active).
   * Used after login so trial workspaces that already added a team are not
   * sent back through private-test setup. Logout wipes local journey cache.
   */
  hasExistingEmployees(): Observable<boolean> {
    return this.projectService.getUserList(0, 0).pipe(
      map((users: any[]) => (users || []).length > 0),
      catchError(() => of(false))
    );
  }

  // Intelligent detection: Just check if user has data, backend will handle the rest
  detectCompletedSteps(): Observable<void> {

    // Simply fetch employees to check if account has data
    // The backend next-step endpoint will intelligently detect what's completed
    return this.projectService.getUserList(0, 0, true).pipe(
      tap((users: any[]) => {
        const employees = users || [];

        const state = this.getState();

        // Log what we detected for debugging
        const trackingMode = this.getTrackingMode();
        const hasScreenshots = employees.some(
          emp =>
            emp.meta?.last_screenshot ||
            emp.meta?.last_application_name ||
            emp.meta?.screenshot_count > 0 ||
            emp.meta?.total_screenshots > 0
        );


        // Apply conservative local completion rules so UX remains correct even if
        // backend sync is delayed:
        // - If tracking mode exists, mark mode_selection complete.
        // - If any employee exists, mark create_employee complete.
        // - If any screenshots exist, consider onboarding finished (per product requirement).
        if (trackingMode) {
          const s = state.steps.find(x => x.id === 'mode_selection');
          if (s) s.completed = true;
        }
        if (employees.length > 0) {
          const s = state.steps.find(x => x.id === 'create_employee');
          if (s) s.completed = true;
        }
        if (hasScreenshots) {
          state.steps.forEach(s => s.completed = true);
          this.setPermanentlyCompleted();
        }
        this.saveState(state);

        // Let the backend handle detection via next-step endpoint
        // No need to make multiple API calls here
      }),
      map(() => undefined)
    );
  }
}
